import { API_URL } from '../../../app-config/constants';
import type { LegacyReview } from '../../react/features/Reviews/types';
import { get, post, put, remove } from '../request';

const BASE_URL = `${API_URL}/review-cycles`;

getReview.operation = 'READ';
export function getReview(cycleId: string, reviewId: string): Promise<LegacyReview> {
    return get(`${BASE_URL}/${cycleId}/reviews/${reviewId}`);
}

listAllReviews.operation = 'READ';
export function listAllReviews(cycleId: string): Promise<LegacyReview[]> {
    return get(`${BASE_URL}/${cycleId}/reviews`);
}

shareWithReviewee.operation = 'COMMAND';
export function shareWithReviewee(cycleId: string, reviewId: string): Promise<LegacyReview> {
    return put(`${BASE_URL}/${cycleId}/reviews/${reviewId}/shared?target=reviewee`);
}

export function revokeShareWithReviewee(cycleId: string, reviewId: string): Promise<void> {
    return remove(`${BASE_URL}/${cycleId}/reviews/${reviewId}/shared?target=reviewee`);
}

export function signManager(cycleId: string, reviewId: string): Promise<void> {
    return put(`${BASE_URL}/${cycleId}/reviews/${reviewId}/signed?target=manager`);
}

export function shareWithManager(cycleId: string, reviewId: string): Promise<void> {
    return put(`${BASE_URL}/${cycleId}/reviews/${reviewId}/shared?target=manager`);
}

setBulkTimelineExtension.operation = 'COMMAND';
export function setBulkTimelineExtension(
    cycleId: string,
    timelineExtensions: {
        reviewKey: string;
        revieweeExtension: string | null;
        reviewerExtension: string | null;
    }[]
) {
    return post<LegacyReview[]>(`${API_URL}/review-cycle/${cycleId}/timeline-extension`, {
        postData: { extensions: timelineExtensions },
    });
}

changeManagerRatings.invalidates = ['listAllReviews'];
export function changeManagerRatings(
    cycleId: string,
    ratings: { [reviewId: string]: string }
): Promise<LegacyReview[]> {
    return put(`${BASE_URL}/${cycleId}/changeManagerRatings`, {
        postData: { managerOverallRatingCodesByReview: ratings },
    });
}

changeManagerRating.operation = 'COMMAND';
export function changeManagerRating(
    reviewId: string,
    cycleId: string,
    managerRatingCode: string,
    isOverride: boolean = false
): Promise<LegacyReview> {
    return put(`${BASE_URL}/${cycleId}/reviews/${reviewId}/changeManagerRating?isOverride=${isOverride}`, {
        postData: { managerRatingCode },
    });
}

changeSecondaryManagerRating.operation = 'COMMAND';
export function changeSecondaryManagerRating(
    reviewId: string,
    cycleId: string,
    managerRatingCode: string,
    isOverride: boolean = false
): Promise<LegacyReview> {
    return put(`${BASE_URL}/${cycleId}/reviews/${reviewId}/changeSecondaryManagerRating?isOverride=${isOverride}`, {
        postData: { managerRatingCode },
    });
}

export function revokeShareWithManager(cycleId: string, reviewId: string): Promise<void> {
    return remove(`${BASE_URL}/${cycleId}/reviews/${reviewId}/shared?target=manager`);
}

export function signReviewee(cycleId: string, reviewId: string): Promise<void> {
    return put(`${BASE_URL}/${cycleId}/reviews/${reviewId}/signed?target=reviewee`);
}

// TODO: Properly handle this in the backend! Currently nudging happens asynchronously
// and the returned entity is not updated yet.

const fakeNudgeReviewee = (review: LegacyReview) => {
    review.revieweeNudgedDate = Date.now();
    review.permissions.canInformReviewee = false;
    review.permissions.canNudgeReviewee = false;
    return review;
};

const fakeNudgeReviewer = (review: LegacyReview) => {
    review.managerNudgedDate = Date.now();
    review.permissions.canInformManager = false;
    review.permissions.canNudgeManager = false;
    return review;
};

nudgeReviewee.operation = 'COMMAND';
export function nudgeReviewee(cycleId: string, reviewId: string) {
    return post<LegacyReview>(`${BASE_URL}/${cycleId}/reviews/${reviewId}/nudge?target=reviewee`).then(
        fakeNudgeReviewee
    );
}

nudgeReviewer.operation = 'COMMAND';
export function nudgeReviewer(cycleId: string, reviewId: string) {
    return post<LegacyReview>(`${BASE_URL}/${cycleId}/reviews/${reviewId}/nudge?target=manager`).then(
        fakeNudgeReviewer
    );
}

completeReview.operation = 'COMMAND';
export function completeReview(cycleId: string, reviewId: string) {
    return put<LegacyReview>(`${BASE_URL}/${cycleId}/reviews/${reviewId}/completed`);
}

reopenReview.operation = 'COMMAND';
export function reopenReview(cycleId: string, reviewId: string) {
    return remove<LegacyReview>(`${BASE_URL}/${cycleId}/reviews/${reviewId}/completed`);
}

revokeRevieweeSign.operation = 'COMMAND';
export function revokeRevieweeSign(cycleId: string, reviewId: string) {
    return remove<LegacyReview>(`${BASE_URL}/${cycleId}/reviews/${reviewId}/signed?target=reviewee`);
}

revokeReviewerSign.operation = 'COMMAND';
export function revokeReviewerSign(cycleId: string, reviewId: string) {
    return remove<LegacyReview>(`${BASE_URL}/${cycleId}/reviews/${reviewId}/signed?target=manager`);
}

deleteReview.operation = 'DELETE';
// BEWARE of swapped arg order to correctly update ladda
export function deleteReview(reviewId: string, cycleId: string) {
    return remove<void>(`${BASE_URL}/${cycleId}/reviews/${reviewId}`);
}

moveReview.operation = 'COMMAND';
moveReview.invalidates = ['listAllReviews'];
export function moveReview(cycleId: string, reviewId: string, newCycleId: string) {
    return put<LegacyReview>(`${BASE_URL}/${cycleId}/reviews/${reviewId}/cycle`, { getData: { newCycleId } });
}

updateLaddaCache.operation = 'UPDATE';
export function updateLaddaCache(review: LegacyReview) {
    return Promise.resolve(review);
}
